//Import Stores
import { get } from 'svelte/store';
import { req_locked_devices, auth_unlocked_at } from 'src/stores/device-runtime/local-auth';
import {
	updating_devices,
	pending_commands,
	rebooting_devices,
	firmwares_gen_2,
	wifi_updating_devices,
} from 'src/stores/device-runtime/process';
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { ai_model_ready } from 'src/stores/features/ai-model';
import { devices } from 'src/stores/lists/devices';
import { scenes } from 'src/stores/lists/scenes';
import { presenceStore } from '../components/Radar/stores/presence';
import {
	cloud_disabled_devices,
	pending_connection_devices,
} from 'src/stores/device-runtime/firmware-ui';

import { on_update } from '../helpers/lists_update';
import {
	handleHousingSocketEvent,
	isHousingSocketEvent,
} from 'src/helpers/houses/housing_socket_events';
import {
	handleProfileEmailChanged,
	isProfileEmailChangedEvent,
} from 'src/helpers/profile_socket_events';
import { requestEditDevice, requestEditSharedDevice } from 'src/components/Lists/list-helpers';

import type { Device } from '../types/Device';

import { transform_ext_humidity, transform_ext_temperature } from '../helpers/ext_sensor_helpers';
import { syncAppNamesFromDeviceSettings } from '../helpers/device_name_sync_helpers';

import {
	real_id,
	shellyId16,
	is_loqed,
	isWallDisplay,
	isPill,
	isXMODDevice,
	processXMODStatus,
	isCamera,
} from '../helpers/interface_helpers';
import { checkMode } from '../helpers/mode_switch_helpers';
import {
	handleUpdatingState,
	handleUpdateProgress,
	removeUpdatingState,
	handleBluTRVUpdate,
	removeTRVUpdatingState,
} from 'src/helpers/firmware_helpers';
import { handleLoqedStatusUpdate } from 'src/helpers/loqed_helpers';
import { handle_socket_event_response } from './socket_commands';
import { decimal_to_hex } from 'src/helpers/utilities';
import { toast } from '@Shelly/shelly-design-system';
import { getVirtualDevicesByParentID } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { AI_FAILURE_REASONS } from 'src/constants';
import { format } from 'svelte-i18n';
import { updateWallDisplayChannels } from 'src/helpers/wall_display_extract/extracting_helpers';
import { handleCameraStatusUpdate } from 'src/components/Camera/helpers/camera-events';
import type { CameraDeviceStatus } from 'src/components/Camera/types/camera-status';
import { updateXMODChannels } from 'src/helpers/xmod_extract/extracting_helpers';
import { doNotShowInitCalibrationModal, showCalibrationHasStartedModal } from 'src/stores';
import { eventLogStore } from 'src/stores/features/event-log';
import { getSocketEventDeviceId } from 'src/components/EventLog/event_log_version_helpers';
import isDimmerWithCalibration from 'src/helpers/is_calibratable_dimmer';
import { handlePillStatusEvent } from 'src/helpers/thepill/thepill_events';
import { mergePartialDeviceStatus } from 'src/helpers/operational_counters_helpers';

let _devices_status = {}; //= subscribe(devices_status);
devices_status.subscribe((val) => {
	_devices_status = val;
});

// For cases, where we want to take events from the socket
// and add them to the event_log store
// for live tracking

/**
 * Handles various WebSocket events from Shelly devices and processes them accordingly.
 *
 * @param message - The WebSocket message object containing event data
 *
 * Handles the following event types:
 * - Shelly:JrpcResponse/CommandResponse/XPResponse/XZResponse - Handles response messages
 * - Shelly:Status/StatusOnChange - Updates device status, handles updating states, wifi updates, and calibration
 * - Shelly:Settings - Processes device settings updates and configurations
 * - Shelly:Sleeping - Updates device sleeping state
 * - Shelly:Online - Manages device online/offline status and related states
 * - Shelly:BulkCmdInfo - Logs bulk command information
 * - Shelly:PendingCommands - Handles pending command counts
 * - Scene:UI - Processes scene-related updates and thermostat settings
 * - api2ui - Handles API to UI updates (lists, housing lifecycle, profile email change)
 * - Shelly:AI - Manages AI-related events like model training and failure detection
 * - Shelly:Gen2Report - Handles firmware updates and progress reporting
 *
 * The function maintains device states across various stores including:
 * - Device status
 * - Device settings
 * - Updating states
 * - Connection states
 * - Cloud connectivity
 * - Firmware updates
 * - AI model status
 *
 * @remarks
 * This is a central event handler that coordinates device state management across the application.
 * It includes special handling for different device generations (Gen1, Gen2) and device types
 * (like Loqed, BluTRV, Wall Display).
 */
export const handle_events = (message) => {
	let id = '';
	if (typeof message.device !== 'undefined') {
		id = shellyId16(message.device.id);
	}

	let _device = get(devices)[id];

	// console.log('!!!!!!!!',message.event, 'message', message);

	switch (message.event) {
		case 'Shelly:JrpcResponse':
		case 'Shelly:CommandResponse':
		case 'Shelly:XPResponse':
		case 'Shelly:XZResponse':
			console.log(`[SOCKET] ${message.event} `, message);

			handle_socket_event_response(message);
			break;
		case 'User:EventLog': {
			const deviceId = eventLogStore.deviceId;
			const socketEvent = message.data[0];
			const socketDeviceId = getSocketEventDeviceId(socketEvent?.p, socketEvent?.tag);

			if (deviceId == socketDeviceId) {
				eventLogStore.addEvent(socketEvent);
			}
			break;
		}
		case 'Shelly:Status':
		case 'Shelly:StatusOnChange':
			// Process presence data for S4SN-0U61X devices
			if (message.device?.code === 'S4SN-0U61X') {
				presenceStore.updateFromSocketEvent(message);
			}

			// ToDo: Should abstract "lists clear at status". can be used here, initial data load and cloud disabled polling
			// remove the id from the updating list on status event for Gen 1 devices
			const isUpdating = typeof get(updating_devices)[id] !== 'undefined';
			if (
				isUpdating &&
				message.device.gen === 'G1' &&
				message.status?.update?.status !== 'updating'
			) {
				removeUpdatingState(get(devices)[id]);
			}

			// remove id from the pending commands list on a status event for Gen 2> devices
			if (
				message.device.gen === 'G2' &&
				isUpdating &&
				Date.now() > get(updating_devices)[id]?.ts + 1 * 60 * 1000
			) {
				// updating_devices.remove(id);
				const deviceProg = get(updating_devices)[id]?.prog;

				if (typeof deviceProg !== 'undefined' && (deviceProg == 0 || deviceProg >= 101)) {
					removeUpdatingState(get(devices)[id]);
					firmwares_gen_2.remove(id);
				}
			}

			// remove the id from the pending connection list on status event
			if (get(pending_connection_devices).indexOf(id) > -1) {
				pending_connection_devices.remove(id);
			}

			// Handle Loqed status
			if (is_loqed(message.device)) {
				message.status = handleLoqedStatusUpdate(message.status, id);
			}

			// // remove id from the pending commands list on a status event
			// if (typeof get(updating_devices[id]) !== 'undefined') {
			// 	updating_devices.remove(id);
			// }

			if (
				get(wifi_updating_devices).some(
					(updating_data: { [id: string]: { channel: number } }) =>
						Object.keys(updating_data)[0] === id,
				)
			) {
				let index, ssid, sta;
				get(wifi_updating_devices).forEach(
					(
						updating_data: {
							[id: string]: { channel: number; desired_wifi: string };
						},
						i,
					) => {
						if (Object.keys(updating_data)[0] === id) {
							index = i;
							sta = Object.values(updating_data)[0].channel;
							ssid = Object.values(updating_data)[0].desired_wifi;
						}
					},
				);
				if (
					ssid ===
						devices_settings.getByRealId(id)?.[sta === 0 ? 'wifi_sta' : 'wifi_sta1']?.ssid ||
					ssid === devices_settings.getByRealId(id)?.wifi[sta === 0 ? 'sta' : 'sta1']?.ssid
				) {
					wifi_updating_devices.remove(id);
				}
			}

			message.status.online = true;
			// Assign message.status._updated a timestamp in UTC
			let now = +(Date.now() / 1000).toFixed(0);
			message.status._updated = now;

			let newStatus = mergePartialDeviceStatus(devices_status.getByRealId(id), message.status);

			if (isPill(message?.device?.code)) {
				newStatus = handlePillStatusEvent(message); // Skips status merge
			}

			const isCameraDevice = isCamera(message.device?.code);

			if (isCameraDevice) {
				newStatus = message?.status || {}; // Skips status merge for camera
				handleCameraStatusUpdate(id, newStatus as CameraDeviceStatus);
			}

			devices_status.queue_device_status_update({ id: id, msg: newStatus });

			if (isXMODDevice(_device?.type)) {
				(async () => {
					try {
						const currentStatus = devices_status.getByRealId(id);
						const processedStatus = await processXMODStatus(_device, currentStatus);
						if (processedStatus !== currentStatus) {
							devices_status.queue_device_status_update({ id: id, msg: processedStatus });
						}
					} catch (error) {
						console.error('[xcspec] Error applying xcspec in socket event:', error);
					}
				})();
			}

			if (isWallDisplay(_device?.type)) {
				const settings = devices_settings.getByRealId(id);
				updateWallDisplayChannels(id, settings, newStatus);
			}

			if (isXMODDevice(_device?.type)) {
				const settings = devices_settings.getByRealId(id);
				updateXMODChannels(id, settings, newStatus);
			}

			let _isDimmerWithCalibration = isDimmerWithCalibration(message.device.code);

			if (_device && !_device?.cloud_online) {
				_device.cloud_online = true;
				updateCloudOnline(_device);
			}

			// show showCalibrationHasStartedModal if calibration has started,
			// but only if the device is not uncalibrated or has no cal_abort error
			// that means the device is new or rebooted
			// and the user started the calibration from the toggle on button
			//pro dimmers starts calibration with 5 and ends with 94
			if (_isDimmerWithCalibration && get(doNotShowInitCalibrationModal) === false) {
				let channel = get(showCalibrationHasStartedModal).channel;

				let progress = message.status?.[`light:${channel}`]?.calibration?.progress;
				if (progress >= 0 && progress < 90) {
					//the modal will be shown if the progress is between 0 and 90
					//pro dimmers starts calibration with 5 and ends with 94
					const lightStatus = message.status?.[`light:${channel}`];
					const hasNoCalAbortError = !lightStatus?.errors?.some((str: string | string[]) =>
						str.includes('cal_abort'),
					);
					const isUncalibrated = lightStatus?.flags?.includes('uncalibrated');
					if (!isUncalibrated || (hasNoCalAbortError && isUncalibrated)) {
						showCalibrationHasStartedModal.set({
							visible: true,
							device: _device,
							channel: channel,
						});
					} else {
						//if the device is uncalibrated and/or has cal_abort error appears while calibration
						showCalibrationHasStartedModal.set({
							visible: false,
							device: null,
							channel: null,
						});
					}
				} else if (
					typeof message.status?.[`light:${channel}`]?.calibration?.progress === 'undefined' &&
					message.oldStatus?.[`light:${channel}`]?.calibration?.progress > 90
				) {
					//the modal will be hidden if the old progress is more than 90 and the current progress is over
					showCalibrationHasStartedModal.set({
						visible: false,
						device: null,
						channel: null,
					});
				}
			}
			break;
		case 'Shelly:Settings':
			if (get(req_locked_devices).indexOf(real_id(id)) !== -1) {
				req_locked_devices.remove(real_id(id));
			}

			if (
				typeof get(updating_devices)[id] !== 'undefined' &&
				// we can check the firmware version from DeviceInfo because this is always added by the cloud infrastructure and only cloud connected devices updates will be sent trough the socket.
				((message.settings?.DeviceInfo !== 'undefined' &&
					message.settings?.DeviceInfo?.fw_id === get(updating_devices)[id].fw) ||
					Date.now() > get(updating_devices)[id]?.ts + 1 * 60 * 1000)
			) {
				// updating_devices.remove(id);
				removeUpdatingState(get(devices)[id]);
				firmwares_gen_2.remove(id);
			}

			checkMode(id, message.settings);
			message.settings = transform_ext_temperature(message.settings);
			message.settings = transform_ext_humidity(message.settings);

			if (_device && !_device?.cloud_online) {
				_device.cloud_online = true;
				updateCloudOnline(_device);
			}

			if (get(rebooting_devices).indexOf(message.settings.id) !== -1) {
				//this store is for rebooting devices
				//so here we cleaning it when socket receives new settings form id which is inside this store
				rebooting_devices.remove(message.settings.id);
			}
			devices_settings.update_devices_settings(id, message.settings);
			void syncAppNamesFromDeviceSettings(id, message.settings);
			auth_unlocked_at.recheckExpiredAndReLock();
			if (isWallDisplay(_device?.type)) {
				updateWallDisplayChannels(id, message.settings);
			}
			if (isXMODDevice(_device?.type)) {
				updateXMODChannels(id, message.settings);
			}
			break;
		case 'Shelly:Sleeping':
			devices_status.queue_single_stat(id, {
				key: '_sleeping',
				value: message.sleeping,
			});
			break;
		case 'Shelly:Online':
			if (import.meta.env.DEV) {
				console.log(id, 'message.online', message.online);
			}
			/*
			 * Remove id from the pending commands list on an online event.
			 * Does not matter if is a 0 or 1 we are checking that it has reached the cloud.
			 */
			if (get(pending_connection_devices).indexOf(id) > -1) {
				pending_connection_devices.remove(id);
			}

			const _status = devices_status.getByRealId(id) || { online: false }; //added default value to avoid error

			if (message.online === 0) {
				// update the online property in the device status packet form the status list, aka. the device is offline
				if (
					!get(cloud_disabled_devices)[id] &&
					typeof get(updating_devices)[id] === 'undefined'
				) {
					// in case the device is deleted from account, we will get message.online = 0,
					// but the device will be removed from the devices store
					if (get(devices)[id]) {
						_status.online = false;

						if (_device?.cloud_online) {
							_device.cloud_online = false;
							updateCloudOnline(_device);
						}
					}
					// Update offline state for shared Virtual and BluTRV devices.
					else if (
						!_device &&
						_status.online &&
						getVirtualDevicesByParentID(id, get(devices)).length > 0
					) {
						_status.online = false;
					}
				}
			} else {
				/*
				 * remove the id form the 'rebooting_devices' list on a online (1) event.
				 */
				if (get(rebooting_devices).indexOf(id) !== -1) {
					rebooting_devices.remove(id);
				}
				_status.online = true;

				/*
				 * Remove the id from the 'updating_devices' and devices that can be updated
				 * ('firmwares_gen_2') list on an online (1) event.
				 */
				if (
					message.device.gen === 'G2' &&
					typeof get(updating_devices)[id] !== 'undefined' &&
					Date.now() > get(updating_devices)[id]?.ts + 1 * 60 * 1000
				) {
					removeUpdatingState(get(devices)[id]);
					// updating_devices.remove(id);
					firmwares_gen_2.remove(id);
				}

				if (_device && !_device?.cloud_online) {
					_device.cloud_online = true;
					updateCloudOnline(_device);
				}
			}
			devices_status.queue_device_status_update({ id, msg: _status });
			break;
		case 'Shelly:BulkCmdInfo':
			console.log('BulkCmdInfo', message);
			break;
		case 'Shelly:PendingCommands':
			if (message.pending_cmds > 0 || message.pending_cmds < 0) {
				pending_commands.update(id, message.pending_cmds);
			} else if (message.pending_cmds === 0) {
				pending_commands.remove(id);
			}
			break;
		case 'Scene:UI':
			//, 1629874154061, 1629874345416
			// if([1629874241966].includes(Number(message.data.scene))){
			// if (import.meta.env.DEV) {
			// 	console.log('New scene message object [%s] [%j]', message.data.scene, message);
			// }

			let _scenes;

			const scenes_subscribe = scenes.subscribe((val) => {
				_scenes = val;
			});

			if (['tmp_sch', 'enable'].includes(message.data?.notifyUI?.type)) {
				let copy_existing_devices_status_scheme = _devices_status[message.data.notifyUI.scene];
				let copy_existing_scene_status_scheme = _scenes[message.data.notifyUI.scene];

				if (copy_existing_devices_status_scheme !== undefined) {
					// console.log("message ", message.data.notifyUI);
					if (message.data.notifyUI.type === 'tmp_sch') {
						copy_existing_devices_status_scheme.thermostat.int_temp =
							message.data.notifyUI.target_temp;
					}
					//TODO: add check id scenes enable props === to new thermostat status
					//and update it
					if (message.data.notifyUI.type === 'enable') {
						copy_existing_devices_status_scheme.thermostat._enabled =
							message.data.notifyUI.enabled;
						// console.log(message.data.notifyUI.enabled, copy_existing_devices_status_scheme.thermostat[0]._enabled);
					}

					devices_status.update_device_status({
						id: message.data.scene,
						msg: copy_existing_devices_status_scheme,
					});
				} else if (copy_existing_scene_status_scheme !== undefined) {
					if (message.data.notifyUI.type === 'enable') {
						// it's not needed to update the scenes if status is the same, otherwise it will update scenes  multiple times without necessary
						//such case is when socket returns data twice
						if (
							_scenes[copy_existing_scene_status_scheme._id]._enabled !==
							message.data.notifyUI.enabled
						) {
							let newScenes = {
								..._scenes,
								[copy_existing_scene_status_scheme._id]: {
									...copy_existing_scene_status_scheme,
									_enabled: message.data.notifyUI.enabled,
								},
							};
							scenes.update(newScenes);
						}
					}
				} else {
					/**
					 * !IMPORTANT if we have socket event for a temperature scheme which doesn't exist in devices status,
					 *  try to get it from PHP API and set it
					 */
					console.log('Undefined temp scheme ', message.data.scene, message);
					//get status via http get to Spazz and update device_status store
					// postReq(`/device/status?id=${message.data.scene}&virtual=1`, {})
					// .then(res => {
					//     if(res.isok){
					//         devices_status.update_device_status({id: message.data.scene, msg: res.data.device_status})
					//     }
					// })
					// .catch(err => console.log('err on getting thermostat status ', err))
				}
			}
			break;
		case 'api2ui':
			// update lists data
			if (import.meta.env.DEV) {
				console.log('%cAPI2UI', 'color:Aquamarine ; font-size: 16px', message.data);
			}

			if (isHousingSocketEvent(message.data)) {
				void handleHousingSocketEvent(message.data, message.ul);
				break;
			}
			if (isProfileEmailChangedEvent(message.data)) {
				void handleProfileEmailChanged(message.data);
				break;
			}
			on_update(message.data);
			break;
		case 'Shelly:AI':
			if (import.meta.env.DEV) {
				console.log('%cShelly:AI', 'color:Aquamarine ; font-size: 16px', message.data);
			}

			let hexId = decimal_to_hex(message.data.device_id);
			let generatedId = message.data.channel > 0 ? `${hexId}_${message.data.channel}` : hexId;
			let deviceName = get(devices)?.[generatedId]?.name;
			let aiModelReadyDevices = get(ai_model_ready);

			switch (message.data.event) {
				case 'fail_detection':
					let AIFailReasonBENumber: number = message.data.reason_id;
					let AIFailReason = `ai_fail_reason_${AI_FAILURE_REASONS[AIFailReasonBENumber]}`;

					toast.open({
						type: 'warning',
						title: `${deviceName}: ${get(format)(AIFailReason)}`,
					});

					let failDetection: string[] = aiModelReadyDevices[generatedId]?.fail_detection || [];
					//if AI_FAILURE_REASONS[AIFailReasonBENumber] is already in the array, don't push it again
					if (!failDetection.includes(AIFailReason)) {
						failDetection.push(AIFailReason);
						ai_model_ready.update(generatedId, {
							key: 'fail_detection',
							value: failDetection,
						});
					}

					break;
				case 'model_ready':
					toast.open({
						type: 'success',
						title: `'${deviceName}' ${get(format)('ai_model_completed_training', {
							default: 'AI model successfully completed training.',
						})}`,
					});
					ai_model_ready.update(generatedId, {
						key: 'model_ready',
						value: true,
					});
					break;
			}

			break;
		case 'Shelly:Gen2Report':
			if (import.meta.env.DEV && message.type !== 'track') {
				console.log('%cShelly:Gen2Report', 'color: green; font-size: 16px', message);
			}

			//this is for tracking for Shelly Presence
			if (message.data?.component === 'presence') {
				if (message.type == 'track') {
					// console.log('Shelly:Gen2Report track', message);
					let device = message.device.id;
					let data = message.data.object;
					presenceStore.append(device, data);
					break;
				} else if (message.type == 'no_track') {
					// TODO: Very temp fix, until they start sending the no_track message
					// console.log('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!');
					// console.log('Shelly:Gen2Report no_track', message);
					// console.log('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!');
					let device = message.device.id;
					presenceStore.resetTrack(device);
					break;
				}
			}

			// Patch devices_settings when the device pushes a presencezone or presence
			// config change (e.g. after PresenceZone.SetConfig or Presence.SetConfig).
			// The notification arrives as a Gen2Report with the updated component config
			// embedded in message.data.object (or message.data.cfg / message.data.delta).
			const component: string = message.data?.component ?? '';
			if (
				component === 'presence' ||
				/^presencezone:\d+$/.test(component) ||
				/^presence:\d*$/.test(component)
			) {
				const deviceId = real_id(id);
				const currentSettings = devices_settings.getByRealId(id);
				if (currentSettings) {
					// Accept data from whichever field the firmware uses
					const incoming =
						message.data?.object ?? message.data?.cfg ?? message.data?.delta ?? null;
					if (incoming && typeof incoming === 'object') {
						devices_settings.update_devices_settings(deviceId, {
							...currentSettings,
							[component]: {
								...(currentSettings[component] ?? {}),
								...incoming,
							},
						});
					}
				}
			} else if (component === 'sys') {
				// Embedded web / local Sys.SetConfig name changes may arrive as Gen2Report
				const deviceId = real_id(id);
				const currentSettings = devices_settings.getByRealId(id);
				const incoming =
					message.data?.object ?? message.data?.cfg ?? message.data?.delta ?? null;
				if (currentSettings && incoming && typeof incoming === 'object') {
					const nextSettings = {
						...currentSettings,
						sys: {
							...(currentSettings.sys ?? {}),
							...incoming,
							device: {
								...(currentSettings.sys?.device ?? {}),
								...(incoming.device ?? {}),
							},
						},
					};
					devices_settings.update_devices_settings(deviceId, nextSettings);
					void syncAppNamesFromDeviceSettings(deviceId, nextSettings);
				}
			}
			if (
				message.type === 'ota_progress' ||
				(message.type === 'ota_begin' && /^bthomedevice:2\d{2}$/.test(message.data?.component))
			) {
				if (/^bthomedevice:2\d{2}$/.test(message.data?.component)) {
					handleBluTRVUpdate(id, message.data.component, message.data?.progress_percent || 0);
				} else {
					if (typeof get(updating_devices)[id] === 'undefined') {
						handleUpdatingState(_device, '');
					}
					handleUpdateProgress(_device, message.data.progress_percent);
				}
			} else if (message.type === 'ota_error') {
				if (/^bthomedevice:2\d{2}$/.test(message.data?.component)) {
					removeTRVUpdatingState(id, message.data.component, message.data?.msg);
				}
			} else if (message.type === 'ota_success') {
				/*
				 * Sets a success state for updating the device
				 * by setting the progress parameter to 101.
				 * Then after for 3 seconds
				 * removes the device form the list entirely.
				 */
				if (/^bthomedevice:2\d{2}$/.test(message.data?.component)) {
					handleBluTRVUpdate(id, message.data.component, 101, true);
				} else {
					updating_devices.updateProgress(id, 101);
					setTimeout(() => {
						// updating_devices.remove(id);
						removeUpdatingState(get(devices)[id]);
						firmwares_gen_2.remove(id);
					}, 3000);

					if (isWallDisplay(_device?.type)) {
						if (get(devices)?.[id]) {
							_device.cloud_online = false;
							updateCloudOnline(_device);
						}
						const _status = devices_status.getByRealId(id) || { online: false };
						if (_status?.online) {
							_status.online = false;
							devices_status.update_device_status({ id, msg: _status });
						}
					}
				}
			}
			break;
		default:
			console.log(message.event, 'not handled', message);
			break;
	}
};

const updateCloudOnline = async (_device: Device) => {
	if (_device?.shared) {
		await requestEditSharedDevice(_device);
	} else {
		await requestEditDevice(_device);
	}
};
